import { ref, reactive } from "vue";
import { useRoute } from "vue-router";

export function useSidebarLogic(options = {}) {
  const route = useRoute();
  const activeSubSection = ref(null);
  const nestedSubSections = reactive({});
  const dropdownStates = reactive({});
  const showAddMembers = reactive({});

  const toggleSubSection = (sectionId, onAfterToggle) => {
    activeSubSection.value =
      activeSubSection.value === sectionId ? null : sectionId;

    // Execute callback after toggle if provided (e.g., for scrolling)
    if (onAfterToggle && activeSubSection.value === sectionId) {
      setTimeout(() => onAfterToggle(sectionId), 0);
    }
  };

  const toggleNestedSubSection = (sectionId) => {
    nestedSubSections[sectionId] = !nestedSubSections[sectionId];
  };

  const getNestedSubSectionState = (sectionId) => {
    return nestedSubSections[sectionId] || false;
  };

  const handleDropdownOpenChange = (open, optionId) => {
    dropdownStates[optionId] = open;
  };

  const handleModeChange = (mode, optionId) => {
    showAddMembers[optionId] = mode === "add-members";
  };

  const isOptionSelected = (option) => {
    const checkRouteMatch = (routeOption) => {
      if (!routeOption) return false;

      let routePath = routeOption.route;
      let routeQuery = null;

      // Parse route if it contains query params
      if (typeof routePath === "string" && routePath.includes("?")) {
        const [path, queryString] = routePath.split("?");
        routePath = path;
        const params = new URLSearchParams(queryString);
        routeQuery = Object.fromEntries(params.entries());
      } else if (typeof routePath === "object") {
        routeQuery = routePath.query;
        routePath = routePath.path;
      }

      // Check if route path matches
      if (routePath && route.path.startsWith(routePath)) {
        if (route.query.sub_object) {
          return (
            route.query.sub_object === routeOption.slug ||
            route.query.sub_object === routeOption.id
          );
        }

        return !routePath.includes("sub_object=") && !routeQuery?.sub_object;
      }

      // Check match patterns
      if (
        routeOption.matchPatterns?.some((pattern) =>
          route.path.startsWith(pattern)
        )
      ) {
        if (route.query.sub_object) {
          return (
            route.query.sub_object === routeOption.slug ||
            route.query.sub_object === routeOption.id
          );
        }

        return (
          !routeOption.route?.includes("sub_object=") && !routeQuery?.sub_object
        );
      }

      if (routeOption.sub_sections?.length > 0) {
        return routeOption.sub_sections.some((subSection) => {
          const pathMatches =
            (subSection.route && route.path.startsWith(subSection.route)) ||
            subSection.matchPatterns?.some((pattern) =>
              route.path.startsWith(pattern)
            );

          if (pathMatches && route.query.sub_object) {
            // Check both slug and id
            return (
              route.query.sub_object === subSection.slug ||
              route.query.sub_object === subSection.id
            );
          }
          return false;
        });
      }

      return false;
    };

    if (option.sections) {
      return option.sections.some((section) => checkRouteMatch(section));
    }

    return checkRouteMatch(option);
  };

  return {
    activeSubSection,
    nestedSubSections,
    dropdownStates,
    showAddMembers,
    toggleSubSection,
    toggleNestedSubSection,
    getNestedSubSectionState,
    handleDropdownOpenChange,
    handleModeChange,
    isOptionSelected,
  };
}
